/**
 * Uma saída da versão na conferência final (E169.F1, GUS-113, ADR-0117 §2): o exame ou o relatório
 * de risco, com a situação, a prévia para abrir e "Marcar como revisado".
 *
 * A marca só se oferece depois de a prévia ser aberta nesta tela, e o servidor confere de novo: o
 * download da prévia, com os bytes, tem de estar na trilha em nome de quem marca.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CorrigirNaMesa } from '@/components/DocumentoDaMesa/CorrigirNaMesa';
import { BaixarExameEArquivos } from '@/components/PreparacaoDoPacote/BaixarExameEArquivos';
import { Badge, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  ConferenciaFinalError,
  marcarSaidaRevisada,
  type SaidaNaConferenciaFinalData,
  type SituacaoDaSaidaNaConferencia,
} from '@/lib/conferencia_final';
import { salvarArquivoBaixado } from '@/lib/http_download';
import { PacoteDoCasoError, baixarPreviaDoPacote } from '@/lib/preparacao_do_pacote';
import { formatarDataHora } from '@/lib/trilha_format';

const t = ptBR.conferenciaFinal;

const TOM: Record<SituacaoDaSaidaNaConferencia, 'neutral' | 'success' | 'warning' | 'danger'> = {
  nao_se_aplica: 'neutral',
  nao_preparada: 'warning',
  conferencia_pendente: 'danger',
  por_revisar: 'warning',
  revisada: 'success',
};

export interface SaidaDaVersaoProps {
  client: AxiosInstance;
  caseId: string;
  saida: SaidaNaConferenciaFinalData;
  /** A marca foi gravada: a conferência final relê. */
  onMarcada: () => void;
}

function Detalhe({ caseId, saida }: { caseId: string; saida: SaidaNaConferenciaFinalData }) {
  if (saida.situacao === 'revisada' && saida.revisada_por && saida.revisada_em) {
    return (
      <p className="text-caption text-fg-muted" data-testid="saida-revisada-por">
        {t.revisadaPor
          .replace('{nome}', saida.revisada_por)
          .replace('{quando}', formatarDataHora(saida.revisada_em))}
      </p>
    );
  }
  if (saida.situacao === 'nao_preparada') {
    return <p className="text-caption text-fg-muted">{t.naoPreparada}</p>;
  }
  if (saida.situacao === 'conferencia_pendente') {
    return (
      <p className="text-caption text-fg-muted">
        {t.conferenciaPendente}{' '}
        <Link className="text-accent-700 underline" to={`/casos/${caseId}/saidas`}>
          {ptBR.resultadoAuditado.prontidao.abrir.saidas}
        </Link>
      </p>
    );
  }
  return null;
}

/** E196.F1 (GUS-260): o exame com os arquivos recebidos; o ZIP também abre a prévia do exame
 * (a trilha registra os dois downloads). */
function ComOsArquivos({
  client,
  caseId,
  saida,
  onBaixado,
}: Omit<SaidaDaVersaoProps, 'onMarcada'> & { onBaixado: () => void }): JSX.Element | null {
  const comPrevia = saida.situacao === 'por_revisar' || saida.situacao === 'revisada';
  if (saida.saida !== 'exame' || !comPrevia) return null;
  return <BaixarExameEArquivos client={client} caseId={caseId} onBaixado={onBaixado} />;
}

export function SaidaDaVersao({ client, caseId, saida, onMarcada }: SaidaDaVersaoProps) {
  const [aberta, setAberta] = useState(false);
  const [ocupado, setOcupado] = useState<'abrindo' | 'marcando' | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const comPrevia = saida.situacao === 'por_revisar' || saida.situacao === 'revisada';

  async function abrir(): Promise<void> {
    setOcupado('abrindo');
    setErro(null);
    try {
      salvarArquivoBaixado(await baixarPreviaDoPacote(client, caseId, saida.saida));
      setAberta(true);
    } catch (err) {
      // E192.F2: o download pediu a recomposição da prévia com o texto vigente
      const atualizando = err instanceof PacoteDoCasoError && err.motivo === 'atualizando';
      setErro(atualizando ? t.recusa.previa_em_atualizacao : t.recusa.indisponivel);
    } finally {
      setOcupado(null);
    }
  }

  async function marcar(): Promise<void> {
    if (!saida.artefato_id) return;
    setOcupado('marcando');
    setErro(null);
    try {
      await marcarSaidaRevisada(client, caseId, saida.saida, saida.artefato_id);
      onMarcada();
    } catch (err) {
      setErro(t.recusa[err instanceof ConferenciaFinalError ? err.motivo : 'indisponivel']);
      if (err instanceof ConferenciaFinalError && err.motivo === 'previa_de_outra_versao') {
        onMarcada();
      }
    } finally {
      setOcupado(null);
    }
  }

  return (
    <li
      className="flex flex-col gap-sc-2 border-t border-border py-sc-3 first:border-t-0"
      data-testid={`saida-${saida.saida}`}
      data-situacao={saida.situacao}
    >
      <div className="flex flex-wrap items-center justify-between gap-sc-2">
        <span className="text-body-md font-medium text-fg">{t.saidas[saida.saida]}</span>
        <Badge variant={TOM[saida.situacao]}>{t.situacao[saida.situacao]}</Badge>
      </div>
      <Detalhe caseId={caseId} saida={saida} />
      {/* E171.F3 (ADR-0122 §2.7): a redação da saída se corrige na mesa (E202.F3). */}
      <CorrigirNaMesa
        caseId={caseId}
        saida={saida.saida}
        oculto={saida.situacao === 'nao_se_aplica'}
      />
      {comPrevia ? (
        <div className="flex flex-wrap items-center gap-sc-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={ocupado === 'abrindo'}
            disabled={ocupado !== null}
            onClick={() => void abrir()}
            data-testid={`abrir-${saida.saida}`}
          >
            {ocupado === 'abrindo' ? t.abrindo : t.abrir}
          </Button>
          {saida.situacao === 'por_revisar' ? (
            <Button
              type="button"
              size="sm"
              loading={ocupado === 'marcando'}
              disabled={!aberta || ocupado !== null}
              onClick={() => void marcar()}
              data-testid={`marcar-${saida.saida}`}
            >
              {ocupado === 'marcando' ? t.marcando : t.marcar}
            </Button>
          ) : null}
          {saida.situacao === 'por_revisar' && !aberta ? (
            <span className="text-caption text-fg-muted">{t.abraAntes}</span>
          ) : null}
        </div>
      ) : null}
      <ComOsArquivos
        client={client}
        caseId={caseId}
        saida={saida}
        onBaixado={() => setAberta(true)}
      />
      {erro !== null ? (
        <p role="alert" className="text-caption text-danger-700">
          {erro}
        </p>
      ) : null}
    </li>
  );
}
